import React, { Fragment, useMemo } from "react";

import { Accordion, Confirm, Icon, Image, List } from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import Thumbnail from "../../../sg-fileupload/components/Thumbnail";
import { NoteSubmitStatus } from "./NoteSubmitStatus";
import "./_NoteAccordion.less";
import { IAuditNote, TEditableAuditNote } from "../../interfaces/IAuditNote";
import { useBool } from "../../../sg-hooks/useBool";
import ErrorBoundary from "../ErrorBoundary";
import EditableAuditNoteModal from "./EditableAuditNoteModal";
import IFormType from "../../interfaces/IFormType";
import AuditNoteMapper from "../../mappers/AuditNoteMapper";
import { useAuditMetaContext } from "../../context/AuditMetaContext";

export interface NoteAccordionItemProps {
    fieldTitle: string;
    active?: boolean;
    formType: IFormType["id"];
    observationFormReference?: string;

    onRemove?: (noteId: IAuditNote["id"]) => void;
    onUpdate?: (id: number, updates: Partial<TEditableAuditNote>) => void;
    onTitleClick?: () => void;

    note: TEditableAuditNote;
}

export const NoteAccordionItem: React.FC<NoteAccordionItemProps> = ({
    fieldTitle,
    active,
    onTitleClick,
    onRemove,
    onUpdate,

    observationFormReference,
    formType,
    note,
}) => {
    const [confirmDeletion, showConfirmDeletion, hideConfirmDeletion] = useBool();
    const [editNote, showEditModal, closeEditModal] = useBool();
    const auditMetaData = useAuditMetaContext();
    const { t } = useTranslation();

    const texts = {
        edit: t("Muokkaa"),
        delete: t("Poista"),
        cancel: t("Peru"),
        ok: t("Ok"),
        deleteNoteConfirmation: t("Haluatko varmisti poistaa kirjatun huomion?"),
    };

    const remove = () => {
        onRemove && onRemove(note.id);
        hideConfirmDeletion();
    };

    const saveUpdates = (auditNote: TEditableAuditNote) => {
        onUpdate && onUpdate(note.id, auditNote);
        closeEditModal();
    };

    const safetyObservation = note.unsubmitted
        ? note.formData?.makeObservation
        : Boolean(note.relatedObservation);

    const metaData = AuditNoteMapper.editableNoteToMetaData(note);

    const formData = useMemo(() => {
        let data = note.unsubmitted ? note.formData || {} : AuditNoteMapper.domainToFormData(note);
        //   data.target = auditMetaData.metaData.target;
        data.sijainti = auditMetaData.metaData.target;
        data.target = auditMetaData.metaData.target;
        return data;
    }, [note, auditMetaData]);

    return (
        <Fragment>
            <Accordion.Title className="NoteAccordion-item-title" active={active} onClick={onTitleClick}>
                <Icon name="dropdown" />
                <strong> {metaData.title} </strong>
            </Accordion.Title>
            <Accordion.Content className="NoteAccordion-item-content" active={active}>
                <p> {metaData.details} </p>

                {metaData.images ? (
                    <Image.Group>
                        {metaData.images.map((image) => (
                            <Thumbnail
                                key={image.id}
                                className="NoteAccordion-item-image"
                                id={image.id}
                                thumbnailWidth={100}
                            />
                        ))}
                    </Image.Group>
                ) : null}

                {safetyObservation && (
                    <NoteSubmitStatus
                        className="NoteAccordion-item-status"
                        status={note.submitStatus}
                        relatedObservation={!note.unsubmitted ? note.relatedObservation : undefined}
                    />
                )}

                <List className="NoteAccordion-item-action" bulleted horizontal>
                    <List.Item onClick={onUpdate && showEditModal} disabled={!onUpdate}>
                        {texts.edit}
                    </List.Item>

                    {editNote && (
                        <ErrorBoundary onError={closeEditModal} fallback={<Fragment />}>
                            <EditableAuditNoteModal
                                fieldTitle={fieldTitle}
                                auditFormType={formType}
                                observationFormType={observationFormReference}
                                onSubmit={saveUpdates}
                                onCancel={closeEditModal}
                                initialData={formData}
                                initialNote={note}
                            />
                        </ErrorBoundary>
                    )}

                    {onRemove && <List.Item onClick={showConfirmDeletion}>{texts.delete}</List.Item>}
                    <Confirm
                        open={confirmDeletion}
                        onCancel={hideConfirmDeletion}
                        onConfirm={remove}
                        content={texts.deleteNoteConfirmation}
                        confirmButton={texts.ok}
                        cancelButton={texts.cancel}
                    />
                </List>
            </Accordion.Content>
        </Fragment>
    );
};
